'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Button } from '@/components/ui/button';
import { Check, Star, TrendingDown, Globe, ArrowRight, ArrowLeft } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { useRTL } from '@/hooks/useRTL';
import { useLocalizedNumbers } from '@/hooks/useLocalizedNumbers';

gsap.registerPlugin(ScrollTrigger);

const TAB_KEYS = ['single', 'multiple', 'fullMouth'] as const;

export default function CostPackagesSection() {
    const { t } = useTranslation('dental_implants');
    const { isRTL } = useRTL();
    const { localizeText } = useLocalizedNumbers();
    const [activeTab, setActiveTab] = useState('single');
    const sectionRef = useRef<HTMLElement>(null);
    const statsRef = useRef<HTMLDivElement>(null);
    const packagesRef = useRef<HTMLDivElement>(null);

    const packages = {
        single: {
            title: t('pricing.packages.single.title'),
            price: t('pricing.packages.single.price'),
            originalPrice: t('pricing.packages.single.original_price'),
            savings: t('pricing.packages.single.savings'),
            duration: t('pricing.packages.single.duration'),
            features: [
                t('pricing.packages.single.features.0'),
                t('pricing.packages.single.features.1'),
                t('pricing.packages.single.features.2'),
                t('pricing.packages.single.features.3'),
                t('pricing.packages.single.features.4'),
                t('pricing.packages.single.features.5')
            ]
        },
        multiple: {
            title: t('pricing.packages.multiple.title'),
            price: t('pricing.packages.multiple.price'),
            originalPrice: t('pricing.packages.multiple.original_price'),
            savings: t('pricing.packages.multiple.savings'),
            duration: t('pricing.packages.multiple.duration'),
            features: [
                t('pricing.packages.multiple.features.0'),
                t('pricing.packages.multiple.features.1'),
                t('pricing.packages.multiple.features.2'),
                t('pricing.packages.multiple.features.3'),
                t('pricing.packages.multiple.features.4'),
                t('pricing.packages.multiple.features.5')
            ]
        },
        fullMouth: {
            title: t('pricing.packages.full_mouth.title'),
            price: t('pricing.packages.full_mouth.price'),
            originalPrice: t('pricing.packages.full_mouth.original_price'),
            savings: t('pricing.packages.full_mouth.savings'),
            duration: t('pricing.packages.full_mouth.duration'),
            features: [
                t('pricing.packages.full_mouth.features.0'),
                t('pricing.packages.full_mouth.features.1'),
                t('pricing.packages.full_mouth.features.2'),
                t('pricing.packages.full_mouth.features.3'),
                t('pricing.packages.full_mouth.features.4'),
                t('pricing.packages.full_mouth.features.5')
            ]
        }
    };

    const includedServices = [
        t('pricing.included_services.0'),
        t('pricing.included_services.1'),
        t('pricing.included_services.2'),
        t('pricing.included_services.3'),
        t('pricing.included_services.4'),
        t('pricing.included_services.5')
    ];

    useEffect(() => {
        const ctx = gsap.context(() => {
            // Animate savings statistics
            if (statsRef.current?.children) {
                Array.from(statsRef.current.children).forEach((stat, index) => {
                    gsap.fromTo(stat,
                        { opacity: 0, y: 30, scale: 0.9 },
                        {
                            opacity: 1,
                            y: 0,
                            scale: 1,
                            duration: 0.6,
                            delay: index * 0.1,
                            ease: "back.out(1.7)",
                            scrollTrigger: {
                                trigger: statsRef.current,
                                start: "top 80%",
                                toggleActions: "play none none reverse"
                            }
                        }
                    );
                });
            }

            // Animate package cards
            if (packagesRef.current?.children) {
                Array.from(packagesRef.current.children).forEach((card, index) => {
                    gsap.fromTo(card,
                        { opacity: 0, y: 50 },
                        {
                            opacity: 1,
                            y: 0,
                            duration: 0.8,
                            delay: index * 0.2,
                            ease: "power3.out",
                            scrollTrigger: {
                                trigger: packagesRef.current,
                                start: "top 80%",
                                toggleActions: "play none none reverse"
                            }
                        }
                    );
                });
            }
        }, sectionRef);

        return () => ctx.revert();
    }, []);

    const currentPackage = packages[activeTab as keyof typeof packages];

    const handleTabKeyDown = useCallback((e: React.KeyboardEvent, key: typeof TAB_KEYS[number]) => {
        const currentIndex = TAB_KEYS.indexOf(key);
        let newIndex: number | null = null;

        if ((!isRTL && e.key === 'ArrowRight') || (isRTL && e.key === 'ArrowLeft')) {
            newIndex = (currentIndex + 1) % TAB_KEYS.length;
        } else if ((!isRTL && e.key === 'ArrowLeft') || (isRTL && e.key === 'ArrowRight')) {
            newIndex = (currentIndex - 1 + TAB_KEYS.length) % TAB_KEYS.length;
        } else if (e.key === 'Home') {
            newIndex = 0;
        } else if (e.key === 'End') {
            newIndex = TAB_KEYS.length - 1;
        }

        if (newIndex !== null) {
            e.preventDefault();
            setActiveTab(TAB_KEYS[newIndex]);
            const nextTab = document.getElementById(`tab-${TAB_KEYS[newIndex]}`);
            nextTab?.focus();
        }
    }, [isRTL]);

    return (
        <section
            id="cost"
            ref={sectionRef}
            className="py-20 bg-white dark:bg-slate-800 relative overflow-hidden"
        >
            {/* Background decoration */}
            <div className="absolute inset-0 opacity-5 dark:opacity-10">
                <div className={`absolute top-1/4 w-96 h-96 bg-gold rounded-full blur-3xl ${isRTL ? 'left-0' : 'right-0'}`}></div>
            </div>

            <div className="container relative">
                <div className="text-center mb-16">
                    <h2 className={`text-4xl md:text-5xl font-bold text-navy dark:text-white mb-6 font-serif ${isRTL ? 'font-arabic' : ''}`}>
                        {t('pricing.title_part1')}{' '}
                        <span className="text-gold">{t('pricing.title_part2')}</span>
                    </h2>
                    <p className={`text-lg text-slate-600 dark:text-slate-300 max-w-4xl mx-auto leading-relaxed ${isRTL ? 'font-arabic' : ''}`}>
                        {t('pricing.description')}
                    </p>
                </div>

                {/* Savings Statistics */}
                <div ref={statsRef} className="grid md:grid-cols-3 gap-8 mb-16">
                    <div className="text-center p-6 bg-gradient-to-br from-gold/10 to-gold/5 rounded-2xl">
                        <TrendingDown className="w-12 h-12 text-gold mx-auto mb-4" />
                        <div className={`text-3xl font-bold text-gold mb-2 ${isRTL ? 'font-arabic' : ''}`}>{localizeText(t('pricing.statistics.savings.value'))}</div>
                        <p className={`text-slate-600 dark:text-slate-300 ${isRTL ? 'font-arabic' : ''}`}>{t('pricing.statistics.savings.label')}</p>
                    </div>
                    <div className="text-center p-6 bg-gradient-to-br from-gold/10 to-gold/5 rounded-2xl">
                        <Globe className="w-12 h-12 text-gold mx-auto mb-4" />
                        <div className={`text-3xl font-bold text-gold mb-2 ${isRTL ? 'font-arabic' : ''}`}>{localizeText(t('pricing.statistics.experience.value'))}</div>
                        <p className={`text-slate-600 dark:text-slate-300 ${isRTL ? 'font-arabic' : ''}`}>{t('pricing.statistics.experience.label')}</p>
                    </div>
                    <div className="text-center p-6 bg-gradient-to-br from-gold/10 to-gold/5 rounded-2xl">
                        <Star className="w-12 h-12 text-gold mx-auto mb-4" />
                        <div className={`text-3xl font-bold text-gold mb-2 ${isRTL ? 'font-arabic' : ''}`}>{localizeText(t('pricing.statistics.success.value'))}</div>
                        <p className={`text-slate-600 dark:text-slate-300 ${isRTL ? 'font-arabic' : ''}`}>{t('pricing.statistics.success.label')}</p>
                    </div>
                </div>

                {/* Package Tabs */}
                <div className="flex justify-center mb-8">
                    <div role="tablist" aria-label={t('pricing.title_part1')} className={`bg-slate-50 dark:bg-navy-950 rounded-2xl p-2 flex ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
                        {TAB_KEYS.map((key) => {
                            const pkg = packages[key];
                            return (
                                <button
                                    key={key}
                                    id={`tab-${key}`}
                                    role="tab"
                                    aria-selected={activeTab === key}
                                    aria-controls={`tabpanel-${key}`}
                                    tabIndex={activeTab === key ? 0 : -1}
                                    onClick={() => setActiveTab(key)}
                                    onKeyDown={(e) => handleTabKeyDown(e, key)}
                                    className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 ${isRTL ? 'font-arabic' : ''} ${activeTab === key
                                        ? 'bg-gold text-navy shadow-lg'
                                        : 'text-slate-600 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-800'
                                        }`}
                                >
                                    {pkg.title}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Selected Package Display */}
                <div
                    id={`tabpanel-${activeTab}`}
                    role="tabpanel"
                    aria-labelledby={`tab-${activeTab}`}
                    className="max-w-2xl mx-auto mb-16"
                >
                    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-8 border border-slate-200 dark:border-slate-700">
                        <div className="text-center mb-8">
                            <h3 className={`text-2xl font-bold text-navy dark:text-white mb-4 ${isRTL ? 'font-arabic' : ''}`}>
                                {currentPackage.title}
                            </h3>
                            <div className={`flex items-center justify-center mb-4 ${isRTL ? 'space-x-reverse space-x-4' : 'space-x-4'}`}>
                                <span className={`text-4xl font-bold text-gold ${isRTL ? 'font-arabic' : ''}`}>
                                    {localizeText(currentPackage.price)}
                                </span>
                                <span className={`text-xl text-slate-600 dark:text-slate-300 line-through ${isRTL ? 'font-arabic' : ''}`}>
                                    {localizeText(currentPackage.originalPrice)}
                                </span>
                                <span className={`bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-3 py-1 rounded-full text-sm font-semibold ${isRTL ? 'font-arabic' : ''}`}>
                                    {localizeText(t('pricing.save_label'))} {localizeText(currentPackage.savings)}
                                </span>
                            </div>
                            <p className={`text-slate-600 dark:text-slate-300 ${isRTL ? 'font-arabic' : ''}`}>
                                {localizeText(t('pricing.treatment_duration_label'))} {localizeText(currentPackage.duration)}
                            </p>
                        </div>

                        <div className="space-y-3 mb-8">
                            {currentPackage.features.map((feature, index) => (
                                <div key={index} className={`flex items-center ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
                                    <Check className="w-5 h-5 text-gold shrink-0" />
                                    <span className={`text-slate-600 dark:text-slate-300 ${isRTL ? 'font-arabic' : ''}`}>{feature}</span>
                                </div>
                            ))}
                        </div>

                        <Button
                            size="lg"
                            className={`w-full bg-gold hover:bg-gold-600 text-navy py-4 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 ${isRTL ? 'font-arabic' : ''}`}
                        >
                            {t('pricing.view_packages_button')}
                            {isRTL ? (
                                <ArrowLeft className="w-5 h-5 mr-2" />
                            ) : (
                                <ArrowRight className="w-5 h-5 ml-2" />
                            )}
                        </Button>
                    </div>
                </div>

                {/* What's Included */}
                <div className="bg-slate-50 dark:bg-navy-950 rounded-2xl p-8">
                    <h3 className={`text-2xl font-bold text-navy dark:text-white mb-6 text-center ${isRTL ? 'font-arabic' : ''}`}>
                        {t('pricing.included_title')}
                    </h3>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {includedServices.map((service, index) => (
                            <div key={index} className={`flex items-start ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
                                <Check className="w-5 h-5 text-gold mt-0.5 shrink-0" />
                                <span className={`text-slate-600 dark:text-slate-300 ${isRTL ? 'font-arabic' : ''}`}>{service}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
} 
